/**
 * SFW GTM — GA4 ecommerce events (STAUD item schema).
 * view_item_list (Liquid + AJAX), select_item (GA4-event-tracking.liquid).
 */
(function () {
  'use strict';

  const DEDUPE_SESSION = new Set();
  const DEDUPE_THROTTLE = new Map();
  const THROTTLE_MS = 500;
  const VIEW_CART_THROTTLE_MS = 2000;

  let pageData = null;
  let lastCartSnapshot = [];
  let lastViewedVariantId = null;

  function readPageData() {
    const productEl = document.getElementById('sfw-gtm-product-data');
    const cartEl = document.getElementById('sfw-gtm-cart-data');
    let data = {};

    try {
      if (productEl) {
        data = { ...data, ...JSON.parse(productEl.textContent) };
      }
      if (cartEl) {
        const cartPayload = JSON.parse(cartEl.textContent);
        if (cartPayload.currency) data.currency = cartPayload.currency;
        if (cartPayload.total_value != null) data.total_value = cartPayload.total_value;
        if (cartPayload.item_count != null) data.item_count = cartPayload.item_count;
        if (cartPayload.cart) data.cart = cartPayload.cart;
      }
    } catch (e) {
      console.warn('[SFW_GTM] Failed to parse bootstrap data', e);
      return null;
    }

    return data.currency || data.product || data.cart ? data : null;
  }

  function getCurrency() {
    const body = document.body;
    if (body && body.dataset.itemCurrency) return body.dataset.itemCurrency;
    if (pageData && pageData.currency) return pageData.currency;
    if (typeof Shopify !== 'undefined' && Shopify.currency && Shopify.currency.active) {
      return Shopify.currency.active;
    }
    return '';
  }

  function applyCategories(item, collections) {
    if (!collections || !collections.length) return item;
    const keys = ['item_category', 'item_category2', 'item_category3', 'item_category4', 'item_category5'];
    collections.forEach((title, i) => {
      if (title && keys[i]) item[keys[i]] = title;
    });
    return item;
  }

  function stripEmptyCategories(item) {
    const keys = ['item_category', 'item_category2', 'item_category3', 'item_category4', 'item_category5'];
    keys.forEach((key) => {
      if (!item[key] || String(item[key]).trim() === '') {
        delete item[key];
      }
    });
    return item;
  }

  function buildItemFromVariant(product, variant) {
    const sku = variant.sku || variant.id;
    const item = {
      item_id: String(sku),
      item_name: product.title || '',
      item_brand: product.vendor || '',
      price: Number(variant.price) || 0,
      discount: Number(variant.discount) || 0,
      quantity: 1,
    };
    return applyCategories(item, product.collections);
  }

  function buildItemFromCartLine(line) {
    const itemId = line.sku || line.variantId;
    const item = {
      item_id: String(itemId),
      item_name: line.title || '',
      item_brand: line.vendor || '',
      price: Number(line.price) || 0,
      discount: Number(line.discount) || 0,
      quantity: line.quantity || 1,
    };
    return applyCategories(item, line.collections);
  }

  function buildItemFromProductCard(card, fallbackIndex) {
    const itemIndex =
      card.dataset.itemIndex !== undefined && card.dataset.itemIndex !== ''
        ? parseInt(card.dataset.itemIndex, 10)
        : fallbackIndex;

    const item = {
      item_id: card.dataset.itemId,
      item_name: card.dataset.itemName,
      discount: parseFloat(card.dataset.itemDiscount) || 0,
      index: itemIndex,
      item_brand: card.dataset.itemBrand,
      item_category: card.dataset.itemCategory,
      item_category2: card.dataset.itemCategory2,
      item_category3: card.dataset.itemCategory3,
      item_category4: card.dataset.itemCategory4,
      item_category5: card.dataset.itemCategory5,
      price: parseFloat(card.dataset.itemPrice) || 0,
      quantity: 1,
    };

    return stripEmptyCategories(item);
  }

  function buildPromotionPayload(el) {
    const payload = {};
    if (el.dataset.gtmPromotionId) payload.promotion_id = el.dataset.gtmPromotionId;
    if (el.dataset.gtmPromotionName) payload.promotion_name = el.dataset.gtmPromotionName;
    if (el.dataset.gtmCreativeName) payload.creative_name = el.dataset.gtmCreativeName;
    if (el.dataset.gtmCreativeSlot) payload.creative_slot = el.dataset.gtmCreativeSlot;
    if (el.dataset.gtmLocationId) payload.location_id = el.dataset.gtmLocationId;
    return payload;
  }

  function pushEvent(eventName, ecommerce) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ event: eventName, ecommerce: ecommerce });
  }

  function dedupeSession(key) {
    if (DEDUPE_SESSION.has(key)) return true;
    DEDUPE_SESSION.add(key);
    return false;
  }

  function dedupeThrottle(key, ms) {
    const windowMs = ms != null ? ms : THROTTLE_MS;
    const now = Date.now();
    const last = DEDUPE_THROTTLE.get(key);
    if (last && now - last < windowMs) return true;
    DEDUPE_THROTTLE.set(key, now);
    return false;
  }

  function snapshotFromBootstrap(cart) {
    if (!cart || !cart.items) return [];
    return cart.items.map((line) => ({ ...line, priceIsMajor: true }));
  }

  function mapApiItemToLine(item) {
    const unitCents = item.price != null ? item.price : item.final_price;
    return {
      variantId: item.variant_id,
      sku: item.sku || String(item.variant_id),
      title: item.product_title || item.title,
      vendor: item.vendor,
      price: unitCents / 100,
      discount: 0,
      quantity: item.quantity,
      collections: [],
      priceIsMajor: false,
    };
  }

  function isFullCartPayload(cartData) {
    const items = cartData.items;
    const count = cartData.item_count;
    if (!items || count == null) return false;
    return items.length === count;
  }

  function mergeIntoSnapshot(partialItems) {
    partialItems.forEach((line) => {
      const key = String(line.variantId);
      const idx = lastCartSnapshot.findIndex((l) => String(l.variantId) === key);
      const merged = {
        ...line,
        collections:
          idx >= 0 && lastCartSnapshot[idx].collections && lastCartSnapshot[idx].collections.length
            ? lastCartSnapshot[idx].collections
            : line.collections,
      };
      if (idx >= 0) {
        lastCartSnapshot[idx] = merged;
      } else {
        lastCartSnapshot.push(merged);
      }
    });
  }

  function refreshCartSnapshotFromApi() {
    const cartUrl =
      typeof routes !== 'undefined' && routes.cart_url ? `${routes.cart_url}.js` : '/cart.js';
    return fetch(cartUrl)
      .then((response) => response.json())
      .then((cart) => {
        if (cart && cart.items) {
          lastCartSnapshot = cart.items.map(mapApiItemToLine);
        }
      })
      .catch(() => {});
  }

  function findVariantInProduct(product, variantId) {
    if (!product || !product.variants) return null;
    return product.variants.find((v) => String(v.id) === String(variantId));
  }

  function computeCartValue() {
    if (pageData && pageData.total_value != null) {
      return Number(pageData.total_value) || 0;
    }
    let total = 0;
    lastCartSnapshot.forEach((line) => {
      total += (line.price || 0) * (line.quantity || 0);
    });
    return Math.round(total * 100) / 100;
  }

  function pushViewItemForVariant(product, variant) {
    if (!product || !variant || variant.available === false) return;

    const vid = variant.id || product.selectedVariantId;
    const dedupeKey = `view_item:${vid}`;
    if (dedupeSession(dedupeKey)) return;

    const item = buildItemFromVariant(product, variant);
    pushEvent('view_item', {
      currency: getCurrency(),
      value: item.price * (item.quantity || 1),
      items: [item],
    });

    lastViewedVariantId = String(vid);
  }

  function pushAddToCart(line, quantity) {
    const qty = quantity || 1;
    const throttleKey = `add_to_cart:${line.sku || line.variantId}:${qty}`;
    if (dedupeThrottle(throttleKey)) return;

    const item = buildItemFromCartLine(line);
    item.quantity = qty;

    pushEvent('add_to_cart', {
      currency: getCurrency(),
      value: Math.round(item.price * qty * 100) / 100,
      items: [item],
    });
  }

  function pushRemoveFromCart(line, quantity) {
    const qty = quantity || 1;
    const item = buildItemFromCartLine(line);
    item.quantity = qty;

    pushEvent('remove_from_cart', {
      currency: getCurrency(),
      value: Math.round(item.price * qty * 100) / 100,
      items: [item],
    });
  }

  function pushViewCart(source) {
    const isCartPage = document.body.classList.contains('template-cart');
    const dedupeKey = isCartPage ? 'view_cart:page' : `view_cart:drawer:${source || 'open'}`;

    if (isCartPage) {
      if (dedupeSession(dedupeKey)) return;
    } else if (dedupeThrottle('view_cart:drawer', VIEW_CART_THROTTLE_MS)) {
      return;
    }

    const items = lastCartSnapshot.map((line) => buildItemFromCartLine(line));

    pushEvent('view_cart', {
      currency: getCurrency(),
      value: computeCartValue(),
      items: items,
    });
  }

  function pushViewItemListFromContainer(container, listId, listName) {
    if (!container) return;

    const cards = container.querySelectorAll('.product-card-wrapper[data-item-id]');
    if (!cards.length) return;

    const dedupeKey = `view_item_list:${listId || ''}:${location.pathname}:${cards.length}`;
    if (dedupeSession(dedupeKey)) return;

    const items = [];
    cards.forEach((card, index) => {
      if (!card.dataset.itemId || String(card.dataset.itemId).trim() === '') return;
      items.push(buildItemFromProductCard(card, index));
    });

    if (!items.length) return;

    pushEvent('view_item_list', {
      item_list_id: listId || '',
      item_list_name: listName || '',
      currency: getCurrency(),
      items: items,
    });
  }

  function pushViewPromotion(el) {
    const dedupeKey = `view_promotion:${el.dataset.gtmPromotionId || ''}:${el.dataset.gtmCreativeSlot || ''}`;
    if (dedupeSession(dedupeKey)) return;

    pushEvent('view_promotion', buildPromotionPayload(el));
  }

  function pushSelectPromotion(el) {
    pushEvent('select_promotion', buildPromotionPayload(el));
  }

  function pushAddToWishlist(item) {
    if (!item || !item.item_id) return;

    pushEvent('add_to_wishlist', {
      currency: getCurrency(),
      value: Math.round((item.price || 0) * (item.quantity || 1) * 100) / 100,
      items: [item],
    });
  }

  function diffCartAndTrackChanges(newItems) {
    const prevByVariant = new Map();
    lastCartSnapshot.forEach((line) => {
      prevByVariant.set(String(line.variantId), { ...line });
    });

    const newByVariant = new Map();
    newItems.forEach((line) => {
      newByVariant.set(String(line.variantId), { ...line });
    });

    prevByVariant.forEach((prevLine, variantKey) => {
      const newLine = newByVariant.get(variantKey);
      const prevQty = prevLine.quantity || 0;
      const newQty = newLine ? newLine.quantity || 0 : 0;
      const delta = newQty - prevQty;

      if (delta > 0) {
        pushAddToCart(newLine || prevLine, delta);
      } else if (delta < 0) {
        pushRemoveFromCart(prevLine, -delta);
      }
    });

    newByVariant.forEach((newLine, variantKey) => {
      if (!prevByVariant.has(variantKey) && (newLine.quantity || 0) > 0) {
        pushAddToCart(newLine, newLine.quantity);
      }
    });

    lastCartSnapshot = newItems.map((line) => ({ ...line }));
  }

  function handlePartialAdd(event, partialSnapshot) {
    const variantId = event.productVariantId ? String(event.productVariantId) : null;
    let addedLine = null;
    let addedQty = 0;

    if (variantId) {
      const newLine = partialSnapshot.find((l) => String(l.variantId) === variantId);
      const prevLine = lastCartSnapshot.find((l) => String(l.variantId) === variantId);
      if (newLine) {
        addedQty = (newLine.quantity || 0) - (prevLine ? prevLine.quantity || 0 : 0);
        if (addedQty > 0) addedLine = { ...newLine };
      }
    }

    if (!addedLine && partialSnapshot.length) {
      const prevIds = new Set(lastCartSnapshot.map((l) => String(l.variantId)));
      addedLine =
        partialSnapshot.find((l) => !prevIds.has(String(l.variantId))) || partialSnapshot[0];
      if (addedLine) {
        const prevLine = lastCartSnapshot.find(
          (l) => String(l.variantId) === String(addedLine.variantId)
        );
        addedQty = (addedLine.quantity || 0) - (prevLine ? prevLine.quantity || 0 : 0);
        if (addedQty <= 0) addedQty = addedLine.quantity || 1;
      }
    }

    if (!addedLine && variantId && pageData && pageData.product) {
      const variant = findVariantInProduct(pageData.product, variantId);
      if (variant) {
        addedQty = addedQty || 1;
        addedLine = {
          variantId: variantId,
          sku: variant.sku || variant.id,
          title: pageData.product.title,
          vendor: pageData.product.vendor,
          price: variant.price,
          discount: variant.discount || 0,
          quantity: addedQty,
          collections: pageData.product.collections || [],
          priceIsMajor: true,
        };
      }
    }

    if (addedLine && addedQty > 0) {
      pushAddToCart(addedLine, addedQty);
    }

    if (partialSnapshot.length) {
      mergeIntoSnapshot(partialSnapshot);
    }
  }

  function handleCartUpdate(event) {
    if (!event || !event.cartData) return;

    const cartData = event.cartData;
    const newSnapshot = (cartData.items || []).map(mapApiItemToLine);

    if (isFullCartPayload(cartData)) {
      diffCartAndTrackChanges(newSnapshot);
      return;
    }

    if (event.source === 'product-form') {
      handlePartialAdd(event, newSnapshot);
      refreshCartSnapshotFromApi();
      return;
    }

    const partialSources = ['quick-add', 'quick-order-list'];
    if (partialSources.includes(event.source)) {
      handlePartialAdd(event, newSnapshot);
      refreshCartSnapshotFromApi();
      return;
    }

    if (newSnapshot.length) {
      mergeIntoSnapshot(newSnapshot);
    }
  }

  function pushBeginCheckout() {
    const items = lastCartSnapshot.map((line) => buildItemFromCartLine(line));

    pushEvent('begin_checkout', {
      currency: getCurrency(),
      value: computeCartValue(),
      items: items,
    });
  }

  function bindBeginCheckout() {
    document.addEventListener(
      'click',
      (evt) => {
        const btn = evt.target.closest('[name="checkout"]');
        if (!btn) return;
        pushBeginCheckout();
      },
      true
    );
  }

  function bindVariantChange() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, (payload) => {
      const variant = payload && payload.data && payload.data.variant;
      if (!variant || !variant.id) return;
      if (String(variant.id) === String(lastViewedVariantId)) return;

      const product = pageData && pageData.product;
      if (!product) return;

      const price =
        variant.compare_at_price != null && variant.compare_at_price > 0
          ? variant.compare_at_price / 100
          : variant.price != null
            ? variant.price / 100
            : 0;
      const discount =
        variant.compare_at_price > variant.price
          ? (variant.compare_at_price - variant.price) / 100
          : 0;

      pushViewItemForVariant(product, {
        id: variant.id,
        sku: variant.sku || variant.id,
        price: price,
        discount: discount,
        available: variant.available,
      });
    });
  }

  function bindCartUpdate() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, handleCartUpdate);
  }

  function bindCartOpen() {
    document.addEventListener('sfw:cart:open', () => {
      pushViewCart('drawer');
    });
  }

  function bindRecommendationsLoaded() {
    document.addEventListener('sfw:recommendations:loaded', (evt) => {
      const detail = evt.detail || {};
      pushViewItemListFromContainer(detail.container, detail.listId, detail.listName);
    });
  }

  function bindPromotions() {
    const promoElements = document.querySelectorAll('[data-gtm-promotion]');
    if (!promoElements.length) return;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            pushViewPromotion(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px 0px 0px', threshold: 0.5 }
      );
      promoElements.forEach((el) => observer.observe(el));
    } else {
      promoElements.forEach((el) => pushViewPromotion(el));
    }

    document.addEventListener(
      'click',
      (evt) => {
        const promo = evt.target.closest('[data-gtm-promotion-link]');
        if (!promo) return;
        pushSelectPromotion(promo);
      },
      true
    );
  }

  function bindWishlist() {
    document.addEventListener(
      'click',
      (evt) => {
        const trigger = evt.target.closest('[data-gtm-wishlist]');
        if (!trigger) return;

        const card = trigger.closest('.product-card-wrapper[data-item-id]') || trigger;
        if (!card.dataset.itemId || String(card.dataset.itemId).trim() === '') return;

        pushAddToWishlist(buildItemFromProductCard(card, 0));
      },
      true
    );
  }

  function init() {
    pageData = readPageData();
    if (pageData && pageData.cart) {
      lastCartSnapshot = snapshotFromBootstrap(pageData.cart);
    }

    if (pageData && pageData.product) {
      const p = pageData.product;
      pushViewItemForVariant(p, {
        id: p.selectedVariantId,
        sku: p.selectedVariantSku,
        price: p.selectedVariantPrice,
        discount: p.selectedVariantDiscount,
        available: p.selectedVariantAvailable,
      });
    }

    if (document.body.classList.contains('template-cart')) {
      pushViewCart('page');
    }

    bindBeginCheckout();
    bindVariantChange();
    bindPromotions();
  }

  window.SFW_GTM = {
    pushEvent,
    getCurrency,
    pushViewCart,
    pushViewItemListFromContainer,
    pushAddToWishlist,
  };

  bindCartUpdate();
  bindCartOpen();
  bindRecommendationsLoaded();
  bindWishlist();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
